import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtNum } from "../lib/api";
import { PageHeader, Surface, Btn, StatTile } from "../components/Primitives";
import { Printer } from "@phosphor-icons/react";

export default function Reconciliation() {
  const [aircraft, setAircraft] = useState([]);
  const [aid, setAid] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/aircraft").then((r) => {
      setAircraft(r.data);
      if (r.data[0]) setAid(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!aid) return;
    api.get(`/aircraft/${aid}/reconciliation/${year}`).then((r) => setData(r.data));
  }, [aid, year]);

  const totalExpEntries = useMemo(
    () => (data ? Object.values(data.expenses_by_category || {}).reduce((s, v) => s + v, 0) : 0),
    [data]
  );

  const sortedPilotNet = useMemo(
    () => Object.entries(data?.pilot_net || {}).sort((a, b) => b[1].net_amount - a[1].net_amount),
    [data]
  );

  const sortedExpenses = useMemo(
    () => Object.entries(data?.expenses_by_category || {}).sort((a, b) => b[1] - a[1]),
    [data]
  );

  return (
    <div>
      <PageHeader overline="Finance" title="Annual reconciliation"
        subtitle="Per-aircraft contributions vs operating expenses, surplus/deficit, by-pilot net, expense breakdown and recommendation."
        actions={
          <div className="flex gap-2 items-center print:hidden">
            <select value={aid} onChange={(e) => setAid(e.target.value)} className="h-10 bg-[#0A0A0A] border border-zinc-800 px-3 text-sm font-mono" data-testid="recon-aircraft">
              {aircraft.map((a) => <option key={a.id} value={a.id}>{a.registration} · {a.make_model}</option>)}
            </select>
            <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="h-10 bg-[#0A0A0A] border border-zinc-800 px-3 text-sm font-mono" data-testid="recon-year">
              {Array.from({ length: 5 }).map((_, i) => {
                const y = new Date().getFullYear() - i;
                return <option key={y} value={y}>{y}</option>;
              })}
            </select>
            <Btn variant="ghost" onClick={() => window.print()} data-testid="print-recon-btn"><Printer size={14} className="inline mr-1.5" />Print PDF</Btn>
          </div>
        }
      />
      <div className="p-8 space-y-6 print:p-0 print:space-y-4 print:bg-white print:text-black">
        {aircraft.length === 0 && <Surface className="p-12 text-center text-zinc-500 text-sm">No aircraft to reconcile yet.</Surface>}
        {data && (
          <>
            <Surface className="p-4 print:border-2 print:border-black print:bg-white">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1 print:text-black">Annual reconciliation · {data.year}</div>
              <div className="font-mono text-xl text-amber print:text-black">{data.aircraft.registration}</div>
              <div className="text-sm text-zinc-400 print:text-black">{data.aircraft.make_model} · {data.aircraft.home_base || "—"}</div>
            </Surface>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <StatTile testId="recon-settlements" label="Settlements" value={fmtMoney(data.settlements_amount)} sub={`${data.settlements_count} entries`} />
              <StatTile testId="recon-contribs" label="Member contribs" value={fmtMoney(data.contributions_amount)} accent="amber" sub={`${data.contributions_count} entries`} />
              <StatTile testId="recon-expenses" label="Operating expenses" value={fmtMoney(data.expenses_amount)} sub={`${data.expenses_count} entries`} />
              <StatTile testId="recon-surplus" label="Surplus / deficit" value={fmtMoney(data.surplus_amount)} accent={data.surplus_amount >= 0 ? "green" : "red"} />
              <StatTile testId="recon-hours" label="Total flight hours" value={fmtNum(data.total_hours, 1)} accent="amber" />
            </div>

            <Surface className="p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-4">By participant — settlements vs fair-share net</div>
              <div className="text-[10px] text-zinc-500 font-mono mb-3">
                Fair share = {fmtMoney(data.fair_share_amount)} per member ({data.n_active_members} active/training-only)
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                    <th className="py-2 text-left">Pilot</th>
                    <th className="py-2 text-right">Hours</th>
                    <th className="py-2 text-right">Contributed</th>
                    <th className="py-2 text-right">Fair share</th>
                    <th className="py-2 text-right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPilotNet.length === 0 && (
                    <tr><td colSpan="5" className="py-4 text-zinc-500">No data for {year}.</td></tr>
                  )}
                  {sortedPilotNet.map(([name, p]) => (
                      <tr key={name} className="border-b border-zinc-900" data-testid={`recon-pilot-${name}`}>
                        <td className="py-2">{name}</td>
                        <td className="py-2 text-right font-mono text-zinc-400">{fmtNum(p.hours, 1)}</td>
                        <td className="py-2 text-right font-mono">{fmtMoney(p.contributed_amount)}</td>
                        <td className="py-2 text-right font-mono text-zinc-500">{fmtMoney(p.fair_share_amount)}</td>
                        <td className={`py-2 text-right font-mono ${p.net_amount >= 0 ? "text-green-400" : "text-red-400"}`}>
                          {p.net_amount >= 0 ? "+" : ""}{fmtMoney(p.net_amount)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </Surface>

            <Surface className="p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-4">Operating expenses by category</div>
              <table className="w-full text-sm">
                <tbody>
                  {sortedExpenses.length === 0 && (
                    <tr><td className="py-4 text-zinc-500">No expenses recorded for {year}.</td></tr>
                  )}
                  {sortedExpenses.map(([cat, amt]) => {
                      const pct = totalExpEntries > 0 ? (amt / totalExpEntries) * 100 : 0;
                      return (
                        <tr key={cat} className="border-b border-zinc-900" data-testid={`recon-exp-${cat}`}>
                          <td className="py-2 capitalize">{cat}</td>
                          <td className="py-2 w-1/2 px-4">
                            <div className="h-1.5 bg-zinc-900 relative">
                              <div className="absolute inset-y-0 left-0 bg-amber/60" style={{ width: `${pct}%` }} />
                            </div>
                          </td>
                          <td className="py-2 text-right font-mono text-zinc-400 w-16">{pct.toFixed(0)}%</td>
                          <td className="py-2 text-right font-mono">{fmtMoney(amt)}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </Surface>

            <Surface className="p-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2">Recommendation</div>
              <div className="text-lg amber font-mono print:text-black">{data.recommendation}</div>
              <div className="text-xs text-zinc-500 mt-2 font-mono">
                Generated {new Date().toLocaleString("en-GB")} · workspace currency throughout
              </div>
            </Surface>
          </>
        )}
      </div>
    </div>
  );
}
